import { useMemo, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, InputAdornment, MenuItem, Select, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { CreateCustomerRequest, Customer, CustomerGroupTreeNode } from '@cmms/api-client'
import type { Site } from '@cmms/types'
import { motion } from 'framer-motion'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { primary } from '../../theme/palette'
import { usePermissions } from '../../hooks/usePermissions'
import { useCustomerStore } from '../../store/customerStore'
import { buildOrgTree, findOrgNode, ORG_NODE_STYLE, type OrgNode, type OrgNodeKind } from '../../lib/orgStructureTree'
import { OrgStructureTree, type OrgTreeContextAction } from './components/OrgStructureTree'
import { ImportOrgStructureDialog } from './components/ImportOrgStructureDialog'
import { SiteDetail, SiteFormDialog, SITE_COLORS } from './SitesTab'

interface Props {
  siteId: string | null
  onSelectSite: (id: string | null) => void
}

type DialogState =
  | { mode: 'create-group' }
  | { mode: 'create-subgroup'; parentId: string; parentName: string }
  | { mode: 'edit-group'; node: OrgNode }
  | { mode: 'create-customer'; groupId?: string }
  | { mode: 'edit-customer'; customer: Customer }
  | { mode: 'create-site'; customerId: string }
  | { mode: 'edit-site'; site: Site }

/// Unified Group -> Sub Group -> Customer -> Site settings screen —
/// replaces the three separate Groups / Customers / Sites tabs with one
/// tree. Locations stay on their own dedicated page; a Site node here
/// still opens the full `SiteDetail` panel reused from SitesTab.tsx.
export function OrgStructureTab({ siteId, onSelectSite }: Props) {
  const qc = useQueryClient()
  const { can } = usePermissions()

  const groupsQ = useQuery({ queryKey: ['customer-groups-tree'], queryFn: () => api.customerGroups.tree(), staleTime: 30_000 })
  const customersQ = useQuery({ queryKey: ['customers'], queryFn: () => api.customers.list(), staleTime: 30_000 })
  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list({ allCustomers: true }), staleTime: 30_000 })

  const groups: CustomerGroupTreeNode[] = Array.isArray(groupsQ.data) ? groupsQ.data : []
  const customers: Customer[] = Array.isArray(customersQ.data) ? customersQ.data : []
  const sites: Site[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const loading = groupsQ.isLoading || customersQ.isLoading || sitesQ.isLoading

  const tree = useMemo(() => buildOrgTree(groups, customers, sites), [groups, customers, sites])

  // Sidebar switcher scope (Group/Sub Group/Customer, or "All customers"
  // when both are null) — opt-in only. The tree defaults to showing
  // everything since this is a management screen: reorganizing the
  // hierarchy (e.g. moving a customer from one Sub Group to another)
  // needs both branches visible at once. `scopedOnly` lets an admin who
  // wants to focus on their current working context narrow to just it.
  const activeCustomerId = useCustomerStore((s) => s.customerId)
  const groupScope = useCustomerStore((s) => s.groupScope)
  const [scopedOnly, setScopedOnly] = useState(false)

  const activeScopeId = groupScope?.id ?? activeCustomerId ?? null
  const activeScopeName = groupScope?.name
    ?? (activeCustomerId ? customers.find((c) => c.id === activeCustomerId)?.name : undefined)
    ?? null
  const activeScopeNode = activeScopeId ? findOrgNode(tree, activeScopeId) : null
  const displayTree = scopedOnly && activeScopeNode ? [activeScopeNode] : tree

  const [search, setSearch] = useState('')
  const [localSelected, setLocalSelected] = useState<{ kind: OrgNodeKind; id: string } | null>(null)
  const selected = siteId ? { kind: 'site' as const, id: siteId } : localSelected

  const [importOpen, setImportOpen] = useState(false)
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<OrgNode | null>(null)

  function invalidateAll() {
    qc.invalidateQueries({ queryKey: ['customer-groups-tree'] })
    qc.invalidateQueries({ queryKey: ['customers'] })
    qc.invalidateQueries({ queryKey: ['sites'] })
  }

  const createGroupM = useMutation({
    mutationFn: (body: { name: string; parent_id?: string }) => api.customerGroups.create(body),
    onSuccess: () => { invalidateAll(); toast.success('Created'); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const updateGroupM = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.customerGroups.update(id, { name }),
    onSuccess: () => { invalidateAll(); toast.success('Saved'); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const deleteGroupM = useMutation({
    mutationFn: (id: string) => api.customerGroups.remove(id),
    onSuccess: () => { invalidateAll(); setConfirmDelete(null); toast.success('Deleted') },
    onError: (e) => { toast.error(e instanceof ApiError ? e.message : 'Failed to delete'); setConfirmDelete(null) },
  })

  const createCustomerM = useMutation({
    // `slug`/`description` are typed nullable here only so this can
    // share the same onSubmit payload shape as the edit dialog — the
    // dialog's create branch always passes `undefined`, never `null`.
    mutationFn: (body: { name: string; slug?: string | null; description?: string | null; status?: 'active' | 'inactive'; group_id?: string }) =>
      api.customers.create(body as CreateCustomerRequest),
    onSuccess: () => { invalidateAll(); toast.success('Customer created'); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const updateCustomerM = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; slug?: string | null; description?: string | null; status?: 'active' | 'inactive'; group_id?: string | null }) =>
      api.customers.update(id, body),
    onSuccess: () => { invalidateAll(); toast.success('Customer updated'); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const deleteCustomerM = useMutation({
    mutationFn: (id: string) => api.customers.remove(id),
    onSuccess: () => { invalidateAll(); setConfirmDelete(null); toast.success('Customer deleted') },
    onError: (e) => { toast.error(e instanceof ApiError ? e.message : 'Failed'); setConfirmDelete(null) },
  })

  const createSiteM = useMutation({
    mutationFn: (body: Parameters<typeof api.sites.create>[0]) => api.sites.create(body),
    onSuccess: (s) => {
      invalidateAll(); toast.success('Site created'); setDialog(null)
      if (s?.id) { onSelectSite(s.id); setLocalSelected(null) }
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const updateSiteM = useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Parameters<typeof api.sites.update>[1]) => api.sites.update(id, body),
    onSuccess: () => { invalidateAll(); toast.success('Site updated'); setDialog(null) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })
  const deleteSiteM = useMutation({
    mutationFn: (id: string) => api.sites.remove(id),
    onSuccess: () => {
      invalidateAll(); setConfirmDelete(null); toast.success('Site deleted')
      onSelectSite(null); setLocalSelected(null)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Has locations/assets — remove them first.'),
  })

  function handleSelect(node: OrgNode) {
    if (node.kind === 'site') { onSelectSite(node.id); setLocalSelected(null) }
    else { onSelectSite(null); setLocalSelected({ kind: node.kind, id: node.id }) }
  }

  function canFor(kind: OrgNodeKind, action: 'create' | 'update' | 'delete'): boolean {
    const perm = kind === 'group' || kind === 'subgroup' ? 'customer_groups' : kind === 'customer' ? 'customers' : 'sites'
    return can(`${perm}:${action}`)
  }

  function handleAddChild(node: OrgNode) {
    if (node.kind === 'group') setDialog({ mode: 'create-subgroup', parentId: node.id, parentName: node.name })
    else if (node.kind === 'subgroup') setDialog({ mode: 'create-customer', groupId: node.id })
    else if (node.kind === 'customer') setDialog({ mode: 'create-site', customerId: node.id })
  }

  function handleContextAction({ type, node }: OrgTreeContextAction) {
    if (type === 'delete') { setConfirmDelete(node); return }
    if (type === 'edit') {
      if (node.kind === 'group' || node.kind === 'subgroup') { setDialog({ mode: 'edit-group', node }); return }
      if (node.kind === 'customer') {
        const c = customers.find((c) => c.id === node.id)
        if (c) setDialog({ mode: 'edit-customer', customer: c })
        return
      }
      const s = sites.find((s) => s.id === node.id)
      if (s) setDialog({ mode: 'edit-site', site: s })
      return
    }
    handleAddChild(node)
  }

  function handleDeleteConfirm() {
    if (!confirmDelete) return
    if (confirmDelete.kind === 'group' || confirmDelete.kind === 'subgroup') deleteGroupM.mutate(confirmDelete.id)
    else if (confirmDelete.kind === 'customer') deleteCustomerM.mutate(confirmDelete.id)
    else deleteSiteM.mutate(confirmDelete.id)
  }

  const deleting = deleteGroupM.isPending || deleteCustomerM.isPending || deleteSiteM.isPending

  const selectedSite = selected?.kind === 'site' ? sites.find((s) => s.id === selected.id) ?? null : null
  const selectedCustomer = selected?.kind === 'customer' ? customers.find((c) => c.id === selected.id) ?? null : null
  const selectedCustomerNode = selected?.kind === 'customer' ? findOrgNode(tree, selected.id) : null
  const selectedGroupNode = (selected?.kind === 'group' || selected?.kind === 'subgroup')
    ? findOrgNode(tree, selected.id) : null

  const canImport = can('customer_groups:create') && can('customers:create') && can('sites:create') && can('locations:create')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          Groups, Sub Groups, Customers, and Sites — one filterable hierarchy.
        </Typography>
        <Stack direction="row" spacing={1.25}>
          <Tooltip title={canImport ? '' : "You don't have permission"} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button variant="outlined" startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => setImportOpen(true)} disabled={!canImport}
                sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}>
                Import CSV
              </Button>
            </Box>
          </Tooltip>
          <Tooltip title={can('customers:create') ? '' : "You don't have permission"} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button variant="outlined" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setDialog({ mode: 'create-customer' })} disabled={!can('customers:create')}
                sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2 }}>
                New customer
              </Button>
            </Box>
          </Tooltip>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
            <Tooltip title={can('customer_groups:create') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setDialog({ mode: 'create-group' })} disabled={!can('customer_groups:create')}
                  sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>
                  New group
                </Button>
              </Box>
            </Tooltip>
          </motion.div>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 480, display: 'flex', flexDirection: { xs: 'column', md: 'row' },
        border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: 3 }}>
        {loading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0,
              borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
              <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <TextField
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment>,
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 16 }} /></IconButton>
                      </InputAdornment>
                    ) : null,
                    sx: { fontSize: 13.5, borderRadius: 1.5 },
                  }}
                />
              </Box>
              <Stack direction="row" alignItems="center" spacing={1}
                sx={{ px: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                <Switch
                  size="small"
                  checked={scopedOnly}
                  disabled={!activeScopeId}
                  onChange={(e) => setScopedOnly(e.target.checked)}
                />
                <Tooltip title={activeScopeId ? '' : 'Select a Group, Sub Group, or Customer in the sidebar switcher first'} arrow>
                  <Typography sx={{ fontSize: 12, color: activeScopeId ? 'text.secondary' : 'text.disabled', lineHeight: 1.3 }}>
                    {activeScopeId ? <>Show only <strong>{activeScopeName}</strong></> : 'Show only current scope'}
                  </Typography>
                </Tooltip>
              </Stack>
              <OrgStructureTree tree={displayTree} selectedId={selected?.id ?? null} onSelect={handleSelect}
                onContextAction={handleContextAction} can={canFor} search={search} />
            </Box>

            {selectedSite ? (
              <SiteDetail site={selectedSite} color={SITE_COLORS[sites.indexOf(selectedSite) % SITE_COLORS.length]!}
                onEdit={() => setDialog({ mode: 'edit-site', site: selectedSite })}
                onDelete={() => setConfirmDelete({ kind: 'site', id: selectedSite.id, name: selectedSite.name, children: [] })} />
            ) : selectedCustomer && selectedCustomerNode ? (
              <CustomerDetailPane customer={selectedCustomer} node={selectedCustomerNode}
                canEdit={can('customers:update')} canDelete={can('customers:delete')} canAddSite={can('sites:create')}
                onEdit={() => setDialog({ mode: 'edit-customer', customer: selectedCustomer })}
                onDelete={() => setConfirmDelete({ kind: 'customer', id: selectedCustomer.id, name: selectedCustomer.name, children: [] })}
                onAddSite={() => handleAddChild(selectedCustomerNode)}
                onSelectChild={handleSelect} />
            ) : selectedGroupNode ? (
              <GroupDetailPane node={selectedGroupNode}
                canUpdate={can('customer_groups:update')} canDelete={can('customer_groups:delete')} canCreate={can('customer_groups:create')}
                onEdit={() => setDialog({ mode: 'edit-group', node: selectedGroupNode })}
                onDelete={() => setConfirmDelete(selectedGroupNode)}
                onAddChild={() => handleAddChild(selectedGroupNode)}
                onSelectChild={handleSelect} />
            ) : (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select a Group, Customer, or Site to view details.</Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {(dialog?.mode === 'create-group' || dialog?.mode === 'create-subgroup' || dialog?.mode === 'edit-group') && (
        <GroupFormDialog
          state={dialog}
          onClose={() => setDialog(null)}
          loading={createGroupM.isPending || updateGroupM.isPending}
          onSubmit={(name) => {
            if (dialog.mode === 'create-group') createGroupM.mutate({ name })
            else if (dialog.mode === 'create-subgroup') createGroupM.mutate({ name, parent_id: dialog.parentId })
            else updateGroupM.mutate({ id: dialog.node.id, name })
          }}
        />
      )}

      {(dialog?.mode === 'create-customer' || dialog?.mode === 'edit-customer') && (
        <CustomerFormDialog
          mode={dialog.mode === 'create-customer' ? 'create' : 'edit'}
          customer={dialog.mode === 'edit-customer' ? dialog.customer : undefined}
          initialGroupId={dialog.mode === 'create-customer' ? dialog.groupId : undefined}
          onClose={() => setDialog(null)}
          loading={createCustomerM.isPending || updateCustomerM.isPending}
          onSubmit={(data) => {
            if (dialog.mode === 'create-customer') createCustomerM.mutate({ ...data, group_id: data.group_id ?? undefined })
            else updateCustomerM.mutate({ id: dialog.customer.id, ...data })
          }}
        />
      )}

      {(dialog?.mode === 'create-site' || dialog?.mode === 'edit-site') && (
        <SiteFormDialog
          mode={dialog.mode === 'create-site' ? 'create' : 'edit'}
          site={dialog.mode === 'edit-site' ? dialog.site : undefined}
          initialCustomerId={dialog.mode === 'create-site' ? dialog.customerId : undefined}
          onClose={() => setDialog(null)}
          loading={createSiteM.isPending || updateSiteM.isPending}
          onSubmit={(data) => dialog.mode === 'create-site'
            ? createSiteM.mutate(data as Parameters<typeof api.sites.create>[0])
            : updateSiteM.mutate({ id: dialog.site.id, ...data })}
        />
      )}

      <ImportOrgStructureDialog open={importOpen} onClose={() => setImportOpen(false)}
        onImported={() => { invalidateAll(); setImportOpen(false) }} />

      <Dialog open={Boolean(confirmDelete)} onClose={() => !deleting && setConfirmDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Delete {confirmDelete?.name}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>
            {confirmDelete?.kind === 'group' && 'This permanently removes the Group. Blocked if it still has Sub Groups.'}
            {confirmDelete?.kind === 'subgroup' && 'This permanently removes the Sub Group. Blocked if any customers are still assigned to it.'}
            {confirmDelete?.kind === 'customer' && 'This permanently removes the customer. Blocked if it still has sites or other data attached.'}
            {confirmDelete?.kind === 'site' && 'This will permanently delete the site and cascade to all its locations. This cannot be undone.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting} sx={{ fontSize: 13, fontWeight: 600 }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ─── Detail panes ────────────────────────────────────────────────────────────

/// Clickable list of a Group/Sub Group/Customer's direct children —
/// shown in the detail pane so navigating the hierarchy doesn't require
/// going back to the tree for every step down.
function ChildrenList({ items, onSelectChild, emptyLabel }: {
  items: OrgNode[]; onSelectChild: (node: OrgNode) => void; emptyLabel: string
}) {
  if (items.length === 0) {
    return <Typography sx={{ fontSize: 12.5, color: 'text.secondary', py: 2 }}>{emptyLabel}</Typography>
  }
  return (
    <Stack spacing={0.75} sx={{ mt: 1.5 }}>
      {items.map((child) => {
        const { icon: Icon, color } = ORG_NODE_STYLE[child.kind]
        return (
          <Stack key={child.id} direction="row" alignItems="center" spacing={1.25}
            onClick={() => onSelectChild(child)}
            sx={{
              px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer',
              border: '1px solid', borderColor: 'divider',
              '&:hover': { borderColor: 'primary.main', bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' },
            }}>
            <Box sx={{
              width: 26, height: 26, borderRadius: 1, flexShrink: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: `${color}1A`, color,
            }}>
              <Icon sx={{ fontSize: 15 }} />
            </Box>
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500 }} noWrap>{child.name}</Typography>
            {child.children.length > 0 && (
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>{child.children.length}</Typography>
            )}
          </Stack>
        )
      })}
    </Stack>
  )
}

function GroupDetailPane({ node, canUpdate, canDelete, canCreate, onEdit, onDelete, onAddChild, onSelectChild }: {
  node: OrgNode; canUpdate: boolean; canDelete: boolean; canCreate: boolean
  onEdit: () => void; onDelete: () => void; onAddChild: () => void; onSelectChild: (node: OrgNode) => void
}) {
  const isGroup = node.kind === 'group'
  const { icon: Icon, color } = ORG_NODE_STYLE[node.kind]
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 3.5, pb: 2.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2.5}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}1A`, color, flexShrink: 0 }}>
            <Icon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{node.name}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.06em', mt: 0.5 }}>
              {isGroup ? 'Group' : 'Sub Group'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title={canUpdate ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={onEdit} disabled={!canUpdate} sx={{ fontSize: 12.5, fontWeight: 600 }}>Rename</Button>
              </Box>
            </Tooltip>
            <Tooltip title={canDelete ? 'Delete' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton size="small" onClick={onDelete} disabled={!canDelete}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {isGroup
              ? `${node.children.length} Sub Group${node.children.length === 1 ? '' : 's'}`
              : `${node.children.length} customer${node.children.length === 1 ? '' : 's'} assigned`}
          </Typography>
          <Tooltip title={canCreate ? '' : "You don't have permission"} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={onAddChild} disabled={!canCreate}
                sx={{ fontSize: 12, fontWeight: 600 }}>{isGroup ? 'Add Sub Group' : 'Add customer'}</Button>
            </Box>
          </Tooltip>
        </Stack>
        <ChildrenList items={node.children} onSelectChild={onSelectChild}
          emptyLabel={isGroup ? 'No Sub Groups yet.' : 'No customers assigned yet.'} />
      </Box>
    </Box>
  )
}

function CustomerDetailPane({ customer, node, canEdit, canDelete, canAddSite, onEdit, onDelete, onAddSite, onSelectChild }: {
  customer: Customer; node: OrgNode; canEdit: boolean; canDelete: boolean; canAddSite: boolean
  onEdit: () => void; onDelete: () => void; onAddSite: () => void; onSelectChild: (node: OrgNode) => void
}) {
  const { icon: Icon, color } = ORG_NODE_STYLE.customer
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 3.5, pb: 2.5, flexShrink: 0 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2.5}>
          <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${color}1A`, color, flexShrink: 0 }}>
            <Icon sx={{ fontSize: 26 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{customer.name}</Typography>
            {customer.description && <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>{customer.description}</Typography>}
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
              {customer.site_count ?? 0} site{(customer.site_count ?? 0) === 1 ? '' : 's'}
              {customer.group_name ? ` · ${customer.group_name}` : ' · Ungrouped'}
              {customer.status === 'inactive' ? ' · Inactive' : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title={canEdit ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                  onClick={onEdit} disabled={!canEdit} sx={{ fontSize: 12.5, fontWeight: 600 }}>Edit</Button>
              </Box>
            </Tooltip>
            <Tooltip title={canDelete ? 'Delete' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton size="small" onClick={onDelete} disabled={!canDelete}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, '&:hover': { color: 'error.main' } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Sites</Typography>
          <Tooltip title={canAddSite ? '' : "You don't have permission"} arrow>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={onAddSite} disabled={!canAddSite}
                sx={{ fontSize: 12, fontWeight: 600 }}>Add site</Button>
            </Box>
          </Tooltip>
        </Stack>
        <ChildrenList items={node.children} onSelectChild={onSelectChild} emptyLabel="No sites yet." />
      </Box>
    </Box>
  )
}

// ─── Group / Sub Group create-rename dialog ─────────────────────────────────

type GroupDialogState =
  | { mode: 'create-group' }
  | { mode: 'create-subgroup'; parentId: string; parentName: string }
  | { mode: 'edit-group'; node: OrgNode }

function GroupFormDialog({ state, onClose, onSubmit, loading }: {
  state: GroupDialogState; onClose: () => void; onSubmit: (name: string) => void; loading: boolean
}) {
  const [name, setName] = useState(state.mode === 'edit-group' ? state.node.name : '')
  const title = state.mode === 'create-group' ? 'Create a new group'
    : state.mode === 'create-subgroup' ? `New Sub Group under ${state.parentName}`
    : `Rename ${state.node.name}`

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label="Name" required autoFocus fullWidth size="small"
          value={name} onChange={(e) => setName(e.target.value)}
          placeholder={state.mode === 'create-group' ? 'e.g. North Region' : 'e.g. North-East'}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || loading} onClick={() => onSubmit(name.trim())}>
          {loading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Customer create/edit dialog ────────────────────────────────────────────

function flattenSubGroups(tree: CustomerGroupTreeNode[]): { id: string; label: string }[] {
  const out: { id: string; label: string }[] = []
  for (const group of tree) {
    for (const sub of group.children) out.push({ id: sub.id, label: `${group.name} → ${sub.name}` })
  }
  return out
}

function CustomerFormDialog({ mode, customer, initialGroupId, onClose, onSubmit, loading }: {
  mode: 'create' | 'edit'
  customer?: Customer
  initialGroupId?: string
  onClose: () => void
  onSubmit: (data: { name: string; slug?: string | null; description?: string | null; status?: 'active' | 'inactive'; group_id?: string | null }) => void
  loading: boolean
}) {
  const [name, setName] = useState(customer?.name ?? '')
  const [slug, setSlug] = useState(customer?.slug ?? '')
  const [description, setDescription] = useState(customer?.description ?? '')
  const [status, setStatus] = useState<'active' | 'inactive'>(customer?.status ?? 'active')
  const [groupId, setGroupId] = useState<string>(customer?.group_id ?? initialGroupId ?? '')

  const groupsQ = useQuery({ queryKey: ['customer-groups-tree'], queryFn: () => api.customerGroups.tree(), staleTime: 60_000, retry: false })
  const subGroups = flattenSubGroups(Array.isArray(groupsQ.data) ? groupsQ.data : [])

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {mode === 'create' ? 'Create a new customer' : `Edit ${customer?.name}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label="Customer name" required autoFocus fullWidth size="small"
            value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Acme Manufacturing"
          />
          <TextField
            label="Slug (optional)" fullWidth size="small"
            value={slug} onChange={(e) => setSlug(e.target.value)}
            placeholder="acme-mfg"
            helperText="Short identifier used in URLs and exports. Lowercase, no spaces."
          />
          <TextField
            label="Description (optional)" fullWidth size="small" multiline minRows={2}
            value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="Internal notes about this customer"
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 80 }}>Status</Typography>
            <Select value={status} size="small" onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
              sx={{ minWidth: 160 }}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </Stack>
          {subGroups.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, minWidth: 80 }}>Group</Typography>
              <Select value={groupId} size="small" displayEmpty
                onChange={(e) => setGroupId(e.target.value)}
                sx={{ minWidth: 160, flex: 1 }}>
                <MenuItem value=""><em>Ungrouped</em></MenuItem>
                {subGroups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.label}</MenuItem>
                ))}
              </Select>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || loading}
          onClick={() => onSubmit({
            name: name.trim(),
            // On edit, a cleared field must send an explicit `null` —
            // omitting it (`undefined`) means "leave unchanged" to the
            // backend's sparse update, so it would silently survive a
            // "successful" save.
            slug: slug.trim() || (mode === 'edit' ? null : undefined),
            description: description.trim() || (mode === 'edit' ? null : undefined),
            status,
            group_id: groupId || null,
          })}
        >
          {loading ? (mode === 'create' ? 'Creating…' : 'Saving…') : (mode === 'create' ? 'Create customer' : 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
